import { ListTodo, Plus } from "lucide-react";
import { useGetList, useTranslate, useUpdate, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import type { Task } from "../../types";
import { PriorityBadge } from "../PriorityBadge";
import { TaskDialog } from "../tasks/TaskDialog";
import { TaskMeta } from "../tasks/TaskMeta";
import { TaskStatusBadge } from "../tasks/TaskStatusBadge";
import { useRefreshBoard } from "../useRefreshBoard";
import { SectionTitle } from "./SectionTitle";

type DialogState = { open: false } | { open: true; task?: Task };

/** Tasks linked to the card's contact, editable in the task dialog. */
export const CardTasksSection = ({
  contactId,
  salesId,
}: {
  contactId: Identifier;
  salesId?: Identifier;
}) => {
  const translate = useTranslate();
  const refreshBoard = useRefreshBoard();
  const [update] = useUpdate<Task>();
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const { data: tasks = [] } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "due_date", order: "ASC" },
    filter: { contact_id: contactId },
  });

  const toggleDone = (task: Task) =>
    update(
      "tasks",
      {
        id: task.id,
        data: { status: task.status === "done" ? "todo" : "done" },
        previousData: task,
      },
      { onSuccess: () => refreshBoard() },
    );

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle icon={<ListTodo />}>
        {translate("crm.operations.tasks.title")}
      </SectionTitle>
      {tasks.map((task) => (
        <div
          key={task.id}
          className="flex items-start gap-3 rounded-md border px-3 py-2"
        >
          <Checkbox
            className="mt-0.5"
            checked={task.status === "done"}
            onCheckedChange={() => toggleDone(task)}
            aria-label={task.text}
          />
          <button
            type="button"
            className="flex min-w-0 flex-1 flex-col gap-1 text-left"
            onClick={() => setDialog({ open: true, task })}
          >
            <span
              className={cn(
                "truncate text-sm font-medium",
                task.status === "done" && "text-muted-foreground line-through",
              )}
            >
              {task.text}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              <TaskStatusBadge status={task.status ?? "todo"} />
              <PriorityBadge priority={task.priority ?? "low"} />
              <TaskMeta task={task} />
            </span>
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => setDialog({ open: true })}
      >
        <Plus />
        {translate("crm.operations.tasks.add")}
      </Button>
      <TaskDialog
        open={dialog.open}
        task={dialog.open ? dialog.task : null}
        defaults={{ contact_id: contactId, sales_id: salesId }}
        onClose={() => {
          setDialog({ open: false });
          refreshBoard();
        }}
      />
    </section>
  );
};
